import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import config from '../../../../config';
import Translate from '../../../../components/Translate';
import {buildHomeLinks} from './links';
import StatisticCard from '../../../../components-new/general/statistics/StatisticCard';
import SilverButton from '../../../../components-new/buttons/SilverButton';
import '../../../../styles/settings-layout.css';
import RatingStars from '../../../../components-new/general/RatingStars';
import LevelBadge from '../../../../components-new/general/LevelBadge';
import {getMyScore, getOperatorsTimes, getStatisticNumbers} from '../../../../services/statistics/api';
import {
    getCurrentChatLimit,
    getDailyChatLimit,
    getOperatorCurrentStatusHistory
} from '../../../../services/company/api';
import {formatSecondsToHHMMSS, getTodayDate, getTomorrowDate} from '../../../../utils/General';
import Colors from '../../../../utils/Colors';
import ForumIcon from '../../../../assets/icons/ForumIcon';
import ColorIndicator from '../../../../components-new/general/ColorIndicator';
import CheckIcon from '../../../../assets/icons/CheckIcon';

const REFRESH_MS = 30000;

const EMPTY_NUMBERS = {
    allRequests: 0,
    todaysRequests: 0,
    answeredRequests: 0,
    unansweredRequests: 0,
    activeRequests: 0,
    forwardedRequests: 0,
};

const EMPTY_TIMES = {avgResponseTime: null, avgFirstAnswerTime: null, avgConversationTime: null};

/*
 * A limit is stored as a number whatever it means, and anything from 999 up is
 * the way "no limit" is written. My Space has always shown it as a word; showing
 * "999" here instead would have read as a real ceiling.
 */
const renderLimit = (limit) => {
    if (limit === null || limit === undefined || limit === '') return '...';
    return limit >= 999 ? <Translate text="unlimited"/> : limit;
};

const duration = (seconds) => (
    seconds === null || seconds === undefined ? '—' : formatSecondsToHHMMSS(seconds)
);

function OperatorHome({user, company}) {
    const subs = company.subs ? company.subs : [];
    const subIds = subs.map(sub => sub.id);

    const [numbers, setNumbers] = useState(EMPTY_NUMBERS);
    const [loadingNumbers, setLoadingNumbers] = useState(true);
    const [mine, setMine] = useState(EMPTY_TIMES);
    const [limits, setLimits] = useState({daily: null, parallel: null});
    const [availableToday, setAvailableToday] = useState(null);

    const features = company.tariff && company.tariff.features ? company.tariff.features : [];
    // the limits are the My Space feature's own endpoints; without it there are
    // no limits to show, and the two tiles come off rather than reading "—"
    const hasLimits = features.indexOf(config.features.MY_HISTORY) !== -1;

    const ratingPercent = Number(user.rating) || 0;
    const ratingCount = Number(user.ratingCount) || 0;

    useEffect(() => {
        let active = true;

        const fetchNumbers = () => {
            getStatisticNumbers(subIds, user.postgresId)
                .then(result => {
                    if (active && result) setNumbers(result);
                })
                .finally(() => {
                    if (active) setLoadingNumbers(false);
                });
        };

        fetchNumbers();
        const interval = setInterval(fetchNumbers, REFRESH_MS);

        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [subIds.join(','), user.postgresId]);

    useEffect(() => {
        let active = true;

        getOperatorsTimes(subIds, [user.postgresId]).then(rows => {
            if (!active) return;

            const row = rows && rows[0];
            setMine(row ? {
                avgResponseTime: parseInt(row.avgResponseTime, 10),
                avgFirstAnswerTime: parseInt(row.avgFirstAnswerTime, 10),
                avgConversationTime: parseInt(row.avgConversationTime, 10),
            } : EMPTY_TIMES);
        });

        return () => {
            active = false;
        };
    }, [subIds.join(','), user.postgresId]);

    useEffect(() => {
        if (!hasLimits) return undefined;

        let active = true;

        Promise.all([getDailyChatLimit(), getCurrentChatLimit()]).then(([daily, parallel]) => {
            if (active) setLimits({daily, parallel});
        });

        return () => {
            active = false;
        };
    }, [hasLimits]);

    /*
     * How much of today was spent available. The endpoint answers with seconds
     * per status for the window asked for; today is the window the rest of this
     * page uses, so there is no picker here - My Space is where a range is
     * chosen.
     */
    useEffect(() => {
        let active = true;

        getOperatorCurrentStatusHistory(getTodayDate(), getTomorrowDate()).then(history => {
            if (!active || !history) return;
            setAvailableToday(history[config.currentStatusMap.available] || 0);
        });

        return () => {
            active = false;
        };
    }, []);

    /*
     * Asked for, not worked out.
     *
     * This screen had its own copy of the formula, assembled from three separate
     * requests - the numbers, the times, the rating. The server answers with the
     * level now, so the badge somebody sees about themselves is the same badge
     * their manager sees in the statistics. They disagreed before, because the
     * two were reading different periods through the same formula.
     */
    const [rank, setRank] = useState(1);

    useEffect(() => {
        let active = true;

        getMyScore()
            .then(result => {
                if (active && result) setRank(result.level);
            })
            .catch(() => {
                /* the badge is the one thing on this page that can be missing
                   without the page being wrong */
            });

        return () => {
            active = false;
        };
    }, []);

    return (
        <div className="stg-page">
            <h1 className="home-greeting">
                <Translate text="dashboardGreeting"/>{user.firstName ? ', ' + user.firstName : ''}
            </h1>

            {/* Side by side while the screen has room for it: these are
                two short blocks, and stacked they pushed everything that
                matters below the fold. */}
            <div className="home-pair">
                <div className="home-section">
                        <h2 className="stg-card__title"><Translate text="dashboardRightNow"/></h2>

                    <div className="stat-card-group">
                        <StatisticCard
                            title="activeRequests"
                            value={numbers.activeRequests}
                            loading={loadingNumbers}
                            icon={<ForumIcon style={{color: Colors.blueDark}}/>}
                        />
                        {hasLimits && (
                            <StatisticCard
                                title="dailyChatsLimit"
                                value={renderLimit(limits.daily)}
                            />
                        )}
                        {hasLimits && (
                            <StatisticCard
                                title="currentChatLimit"
                                value={renderLimit(limits.parallel)}
                            />
                        )}
                    </div>
                </div>

                <div className="home-section">
                        <h2 className="stg-card__title"><Translate text="dashboardToday"/></h2>

                    <div className="stat-card-group">
                        <StatisticCard
                            title="todaysRequestsToOperator"
                            value={numbers.todaysRequests}
                            loading={loadingNumbers}
                            icon={<CheckIcon style={{color: Colors.green}}/>}
                        />
                        <StatisticCard
                            title="dashboardAvailableToday"
                            value={availableToday === null ? '...' : formatSecondsToHHMMSS(availableToday)}
                            /* the status dot, the same one the member list and My
                               Space use - bare, so it keeps its own 8px and gets no
                               box drawn around it */
                            icon={<ColorIndicator currentStatus={config.currentStatusMap.available}/>}
                            iconBare
                        />
                    </div>
                </div>
            </div>

            <div className="stg-grid">
                <div className="home-section">
                    <h2 className="stg-card__title"><Translate text="dashboardMySpeed"/></h2>

                    <div className="stg-list">
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="avrgAnswerSpeed"/></span>
                            <span className="stg-row__actions home-figure">
                                {duration(isNaN(mine.avgResponseTime) ? null : mine.avgResponseTime)}
                            </span>
                        </div>
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="avrgFirstAnswerSpeed"/></span>
                            <span className="stg-row__actions home-figure">
                                {duration(isNaN(mine.avgFirstAnswerTime) ? null : mine.avgFirstAnswerTime)}
                            </span>
                        </div>
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="avrgConvTime"/></span>
                            <span className="stg-row__actions home-figure">
                                {duration(isNaN(mine.avgConversationTime) ? null : mine.avgConversationTime)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="home-section">
                    <h2 className="stg-card__title"><Translate text="dashboardMyRating"/></h2>

                    <div className="stg-list">
                        {/* not the `rating` key: that one reads "Operators
                            rating", which is a column heading in a table of
                            everybody and says the wrong thing on the page of the
                            one person it belongs to */}
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="dashboardRatingScore"/></span>
                            <span className="stg-row__actions">
                                <RatingStars value={(ratingPercent * 5) / 100} starSize="small"/>
                                <span className="home-figure">{ratingPercent}%</span>
                            </span>
                        </div>
                        {/* the number that used to sit in brackets after the
                            stars with nothing to say what it was */}
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="ratingCount"/></span>
                            <span className="stg-row__actions home-figure">{ratingCount}</span>
                        </div>
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="Level"/></span>
                            <span className="stg-row__actions"><LevelBadge level={rank}/></span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="setting-cards">
                {buildHomeLinks(user, company).map(link => {
                    const Icon = link.icon;
                    return (
                        <Link className="home-quick-link" key={link.key} to={link.path}>
                            <SilverButton asDiv variant="outline" width="auto" height="36px">
                                <Icon width={16} height={16}/>
                                <Translate text={link.label}/>
                            </SilverButton>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export default OperatorHome;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/home/OperatorHome.jsx